/**
 * Alarms Service - Irrigo Platform
 * Servicio para gestión de alarmas del sistema
 */

import { api, ApiResponse, PaginatedResponse, PaginationParams } from './api';
import type {
  ApiAlarm,
  AlarmFilters,
  ApiAlarmStats,
  ApiAlarmSummary,
  ResolveAlarmRequest,
} from '@/types/api';

// ============================================================================
// ALARMS SERVICE
// ============================================================================

export const alarmsService = {
  /**
   * Listar alarmas con filtros opcionales
   * El API devuelve { total, items } en lugar de { items, pagination }
   */
  async getAll(params?: AlarmFilters): Promise<PaginatedResponse<ApiAlarm>> {
    // Convertir pageSize a limit (el backend usa 'limit', no 'pageSize')
    const backendParams: Record<string, string | number | boolean | undefined> = params ? { ...params } : {};
    if (backendParams.pageSize !== undefined) {
      backendParams.limit = backendParams.pageSize;
      delete backendParams.pageSize;
    }
    // Añadir page=1 por defecto si no se especifica
    if (backendParams.page === undefined) {
      backendParams.page = 1;
    }
    
    // El API devuelve { total, items } directamente en data
    // Según los ejemplos: { success: true, data: { total: 5, items: [...] } }
    const response = await api.get<{ total: number; items: ApiAlarm[] }>('/alarms', backendParams);
    
    if (!response.success) {
      return {
        success: false,
        error: response.error || { code: 'UNKNOWN_ERROR', message: 'Error al cargar alarmas' },
      };
    }
    
    // Si no hay data, devolver estructura vacía
    if (!response.data) {
      const page = (backendParams.page as number) || 1;
      const limit = (backendParams.limit as number) || 20;
      return {
        success: true,
        data: {
          items: [],
          pagination: {
            page,
            pageSize: limit,
            total: 0,
            totalPages: 0,
          },
        },
      };
    }
    
    // Verificar que response.data tenga la estructura esperada { total, items }
    if (!('total' in response.data) || !('items' in response.data)) {
      // Si es un array directo (caso inesperado), convertirlo
      if (Array.isArray(response.data)) {
        const page = (backendParams.page as number) || 1;
        const limit = (backendParams.limit as number) || 20;
        return {
          success: true,
          data: {
            items: response.data as ApiAlarm[],
            pagination: {
              page,
              pageSize: limit,
              total: response.data.length,
              totalPages: Math.ceil(response.data.length / limit),
            },
          },
        };
      }
      // Estructura desconocida
      return {
        success: false,
        error: { code: 'INVALID_DATA', message: 'Estructura de datos inválida del servidor' },
      };
    }
    
    // Transformar { total, items } a { items, pagination }
    const { total, items } = response.data;
    const page = (backendParams.page as number) || 1;
    const limit = (backendParams.limit as number) || 20;
    const totalPages = Math.ceil(total / limit);
    
    return {
      success: true,
      data: {
        items: items || [],
        pagination: {
          page,
          pageSize: limit,
          total: total || 0,
          totalPages,
        },
      },
    };
  },

  /**
   * Obtener alarma por ID
   */
  async getById(id: string): Promise<ApiResponse<ApiAlarm>> {
    return api.get<ApiAlarm>(`/alarms/${id}`);
  },

  /**
   * Reconocer una alarma (marcar como vista)
   */
  async acknowledge(id: string): Promise<ApiResponse<null>> {
    return api.post<null>(`/alarms/${id}/acknowledge`);
  },

  /**
   * Resolver una alarma (marcar como solucionada)
   */
  async resolve(id: string, data?: ResolveAlarmRequest): Promise<ApiResponse<null>> {
    return api.post<null>(`/alarms/${id}/resolve`, data);
  },

  /**
   * Obtener estadísticas de alarmas
   */
  async getStats(): Promise<ApiResponse<ApiAlarmStats>> {
    return api.get<ApiAlarmStats>('/alarms/stats');
  },

  /**
   * Obtener resumen de alarmas para dashboard
   */
  async getSummary(): Promise<ApiResponse<ApiAlarmSummary>> {
    return api.get<ApiAlarmSummary>('/alarms/summary');
  },
};

export default alarmsService;
